import { later } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';

import type PopupModal from 'webapp/models-custom/popup/modal';

/**
 * Service that manages a queue of automated popups (e.g., promotional messages, announcements).
 *
 * IMPORTANT: This service is specifically for automated/promotional popups.
 * Do NOT use for:
 * - Context menus
 * - User-initiated popups
 *
 * Features:
 * - Maintains an ordered queue of popups with their display conditions
 * - Ensures popups only show on allowed routes (whitelist/blacklist support)
 * - Prevents popup spam with cooldown periods between displays
 * - Automatically triggers next popup when current one closes
 *
 * Each popup in the queue can specify:
 * - Route restrictions (whitelist/blacklist)
 * - Custom display conditions
 * - Required promises that must resolve before showing
 * - Add-to-queue conditions
 */

export interface QueuedPopup {
	popup: PopupModal;
	popupData?: object;
	checkAddConditions: () => boolean; // If true, the popup will be added to the queue, also if false, the popup will be removed from the queue
	checkStartConditions: () => boolean;
	routeWhitelist?: string[];
	routeBlacklist?: string[];
	requiredPromises?: Promise<unknown>[];
	skipCooldown?: boolean; // If true, the popup will skip the cooldown and open immediately if conditions are met
	queueFirst?: boolean; // If true, the popup goes to the front of the queue, ahead of the popups queued before it
}

export default class AutomatedPopupQueueService extends EmberService {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('logger') declare loggerService: Service.Logger;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked queue: QueuedPopup[] = [];

	queuePopup(queuedPopup: QueuedPopup) {
		// prevent showing popups to logged in as power user
		if (this.currentUserService.loggedInAsPowerUser) {
			return;
		}

		// Check if a popup with the same componentName already exists in the queue
		const isDuplicate = this.queue.some(
			(popup) => popup.popup.componentInnerClass === queuedPopup.popup.componentInnerClass,
		);

		if (!isDuplicate && queuedPopup.checkAddConditions()) {
			if (queuedPopup.queueFirst) {
				this.queue.unshift(queuedPopup);
			} else {
				this.queue.push(queuedPopup);
			}
		}
	}

	checkToOpenPopup(): void {
		if (!this.queue.length) {
			return;
		}

		later(this, this.performStartChecks, this.initialCheckDelayMs);
	}

	private triggerCooldownMs = 30000; // 30 seconds cooldown between triggers
	private lastTriggerTimestamp = 0; // timestamp of last trigger
	private initialCheckDelayMs = 500; // default delay for initial checks because of page switch / loading
	private activePopup: PopupModal | null = null;

	private async performStartChecks() {
		if (this.activePopup) {
			return;
		}

		// Clean up queue first - remove popups whose add conditions are no longer met
		this.queue = this.queue.filter((popup) => popup.checkAddConditions());

		const currentTimeMs = Date.now(),
			hasCooldownPassed = currentTimeMs - this.lastTriggerTimestamp >= this.triggerCooldownMs,
			currentRoute = this.navigationService.activatedRoute,
			popupQueue = this.queue;
		// Iterate through the queue to find the first viable popup
		for (const scheduledPopup of popupQueue) {
			// Check if there are pending promises and skip if not all resolved
			if (scheduledPopup.requiredPromises?.length) {
				const promiseStates = scheduledPopup.requiredPromises.map((promise) =>
					promise.then(() => true).catch(() => false),
				);
				const allPromisesResolved = await Promise.all(promiseStates).then((results) =>
					results.every(Boolean),
				);

				if (!allPromisesResolved) {
					continue;
				}
			}

			// Check for conflicting route definitions
			if (
				scheduledPopup.routeBlacklist &&
				scheduledPopup.routeWhitelist &&
				this.isRouteInList(currentRoute, scheduledPopup.routeBlacklist) &&
				this.isRouteInList(currentRoute, scheduledPopup.routeWhitelist)
			) {
				this.loggerService.logToConsole(
					'[Popup Queue] Invalid configuration: Route exists in both blacklist and whitelist',
					{
						route: currentRoute,
						popup: scheduledPopup.popup.componentInnerClass.toString(),
					},
				);
				continue;
			}

			// Check route blacklist
			if (
				scheduledPopup.routeBlacklist &&
				this.isRouteInList(currentRoute, scheduledPopup.routeBlacklist)
			) {
				continue;
			}

			// Check route whitelist
			if (
				scheduledPopup.routeWhitelist &&
				!this.isRouteInList(currentRoute, scheduledPopup.routeWhitelist)
			) {
				continue;
			}

			// Check if the cooldown has passed
			if (!scheduledPopup.skipCooldown && !hasCooldownPassed) {
				continue;
			}

			// another check may have opened a popup while this one awaited the required promises
			if (this.activePopup) {
				return;
			}

			// If the popup passes all checks, open it
			if (scheduledPopup.checkStartConditions()) {
				const popupToOpen = scheduledPopup.popup;

				this.lastTriggerTimestamp = Date.now();

				popupToOpen.open(scheduledPopup.popupData);
				this.activePopup = popupToOpen;

				popupToOpen.on(PopupEvent.CLOSED, this, () => {
					this.activePopup = null;
					this.queueCheckToOpenAfterClosePopup();
				});

				return; // Exit after opening the first viable popup
			}
		}
	}

	private isCheckToOpenPopupAfterCloseQueued = false;

	private queueCheckToOpenAfterClosePopup(): void {
		if (!this.isCheckToOpenPopupAfterCloseQueued) {
			this.isCheckToOpenPopupAfterCloseQueued = true;
			later(
				this,
				() => {
					this.isCheckToOpenPopupAfterCloseQueued = false;
					this.checkToOpenPopup();
				},
				this.triggerCooldownMs,
			);
		}
	}

	private isRouteMatch(route: string, pattern: string): boolean {
		// Handle wildcard pattern (e.g., "metrics.*")
		if (pattern.endsWith('.*')) {
			const basePattern = pattern.slice(0, -2); // Remove ".*"
			return route.startsWith(basePattern);
		}
		// Exact match for specific routes
		return route === pattern;
	}

	private isRouteInList(currentRoute: string, routeList: string[]): boolean {
		return routeList.some((pattern) => this.isRouteMatch(currentRoute, pattern));
	}
}

declare module '@ember/service' {
	interface Registry {
		'automated-popup-queue': AutomatedPopupQueueService;
	}
}
